import { ContactAttemptOptions, PriorityOptions } from 'features/ticket-page/order-intake-form'
import { TicketStatusOptions } from 'features/ticket-page/order-intake-form-edit'
import { TicketEditContext } from 'pages/ticket-edit'
import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ProjectWorkOrderType } from 'types/project.type'
import { useClient } from 'utils/auth-context'
import { ALL_PROJECTS_QUERY_KEY, PROJECTS_QUERY_KEY } from './projects'
import { orderBy } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { getFileContents } from './transactions'
import { DocumentType } from 'types/common.types'
import { dateISOFormat, getLocalTimeZoneDate } from 'utils/date-time-utils'
import { SelectOption } from 'types/transaction.type'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { convertDateTimeToServerISO } from 'components/table/util'
import { PROPERTY_STATUS } from 'features/common/status'

export const PAYMENT_TERMS_OPTIONS = [
  {
    value: 7,
    label: '7',
  },
  {
    value: 10,
    label: '10',
  },
  {
    value: 14,
    label: '14',
  },
  {
    value: 20,
    label: '20',
  },
  {
    value: 30,
    label: '30',
  },
]

export type Ticket = {}

export const TICKETS_QUERY_KEY = 'tickets'
export const ALL_TICKETS_QUERY_KEY = 'all_tickets'

export const useCreateTicket = () => {
  const client = useClient()
  const queryClient = useQueryClient()

  const toast = useToast()

  const apiEndPoint = 'projects' //as tickets

  const { setSelectedTicketId } = useContext(TicketEditContext)

  const navigate = useNavigate()

  return useMutation(
    (payload: any) => {
      return client(apiEndPoint, {
        data: payload,
        method: 'POST',
      })
    },
    {
      onSuccess: (project: any) => {
        setSelectedTicketId(project?.data?.id)
        queryClient.invalidateQueries(ALL_TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(['GetProjectWorkOrders', project?.data?.id])
        queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY)
        queryClient.invalidateQueries(PROJECTS_QUERY_KEY)

        toast({
          title: 'Ticket',
          description: 'New ticket created successfully',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })

        navigate(`/property-details/${project?.data?.propertyId}/ticket/${project?.data?.id}`)
      },
      onError: () => {},
    },
  )
}

export const useUpdateTicket = () => {
  const client = useClient()
  const queryClient = useQueryClient()
  const { setSelectedTicketId } = useContext(TicketEditContext)
  const apiEndPoint = 'projects' //as tickets
  let mc = null
  const { isProjectCoordinator: isMaintenanceCoordinator } = useUserRolesSelector()
  const toast = useToast()
  let isReceivable
  return useMutation(
    (payload: any) => {
      isReceivable = payload.isReceivable
      mc = payload.defaultMaintenanceCordinatorId
      return client(apiEndPoint, {
        data: payload,
        method: 'PUT',
      })
    },
    {
      onSuccess: (project: any) => {
        if (!isReceivable && isMaintenanceCoordinator && mc !== project?.data?.projectCoordinatorId) {
          setSelectedTicketId(null)
          toast({
            title: 'Order Intake',
            description: 'Ticket updated successfully',
            status: 'success',
            isClosable: true,
            position: 'top-left',
          })
        }

        queryClient.invalidateQueries(ALL_TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(['notes', project?.data?.id])
        queryClient.invalidateQueries(['project', project?.data?.id])
        queryClient.invalidateQueries(['project'])
        queryClient.invalidateQueries(['GetProjectWorkOrders', project?.data?.id])
        queryClient.invalidateQueries(['GetProjectWorkOrders', project?.data?.id?.toString()])
        setTimeout(() => {
          queryClient.invalidateQueries(['GetProjectWorkOrders', project?.data?.id])
          queryClient.invalidateQueries(['GetProjectWorkOrders', project?.data?.id?.toString()])

          queryClient.refetchQueries(['GetProjectWorkOrders', Number(project?.data?.id)])
        }, 2000)

        queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY)
        queryClient.invalidateQueries(PROJECTS_QUERY_KEY)
      },
      onError: () => {},
    },
  )
}

export const useGetTicket = (ticketId: number | undefined) => {}

export const useGetAllTickets = (filterQueryString: string, propertyId?: number) => {
  const client = useClient()

  const { data, ...rest } = useQuery<Array<any>>(
    ALL_TICKETS_QUERY_KEY,
    async () => {
      const response = await client(`v1/projects?propertyId.equals=${propertyId}&${filterQueryString}&sort=id,desc`, {})

      return response?.data
    },
    {
      enabled: !!propertyId,
    },
  )

  return {
    data,
    ...rest,
  }
}

export const useGetWOWithTicket = () => {}

export const parseNewTicketFormValuesToPayload = formValues => {
  const subWorkOrders = formValues.tradeAndDescription?.map(o => {
    return { tradeId: o.vendorTrade?.value, issueDescription: o.issueDescription, companyId: o.company?.value }
  })

  return {
    projectType: 105,
    projectStatus: 'NEW',
    projectStatusId: 7,

    sowOriginalContractAmount: formValues.sowOriginalContractAmount,
    propertyId: Number(formValues?.propertyId),
    woNumber: formValues?.ticketId,
    //order intake

    subWorkOrders: subWorkOrders,

    taskPriority: formValues.priority?.value,
    projectManagerId: formValues.maintenanceSpecialist?.value,
    projectCordinatorId: formValues.maintenanceCoordinator?.value,
    clientNteAmount: formValues.clientNTE,
    clientNotes: formValues.clientNotes,
    clientSchedule: formValues.schedule,
    clientCallAttempt1: false,
    clientTextAttempt1: formValues.clientTextAttempt1,
    clientEmailAttempt1: formValues.clientEmailAttempt1,
    clientResponse1: formValues.response1?.value,
    clientResponse2: formValues.response2?.value,
    clientCallAttempt2: false,
    clientTextAttempt2: formValues.clientTextAttempt2,
    clientEmailAttempt2: formValues.clientEmailAttempt2,
    clientLvmAttempt1: formValues.clientEmailAttempt1,
    clientLvmAttempt2: formValues.clientEmailAttempt2,
    forcedStatus: formValues.forcedStatus,
  }
}

export const parseOrderIntakeValuesFromServer = (
  project,
  maintenanceCoordinatorSelectOptions,
  fpmAsMaintenanceSpecialistSelectOptions,
) => {
  const remainingPayment = project.accountRecievable || 0

  return {
    forcedStatus: project?.forcedStatus,
    id: project?.id,
    projectType: 105,
    status: TicketStatusOptions.find(o => o.value === project?.projectStatusId),
    overrideProjectStatus: null,
    sowOriginalContractAmount: project?.sowOriginalContractAmount,
    propertyId: Number(project?.propertyId),
    ticketId: project?.woNumber,
    //order intake
    priority: PriorityOptions.find(o => o.value === project?.taskPriority),
    maintenanceSpecialist: fpmAsMaintenanceSpecialistSelectOptions?.find(o => o.value === project?.projectManagerId),
    maintenanceCoordinator: maintenanceCoordinatorSelectOptions?.find(
      o => o.value === (project?.projectCoordinatorId || project?.projectCordinatorId),
    ),
    defaultMaintenanceCoordinator: maintenanceCoordinatorSelectOptions?.find(
      o => o.value === (project?.projectCoordinatorId || project?.projectCordinatorId),
    ),
    clientNTE: project?.clientNteAmount,
    clientNotes: '',
    schedule: project?.clientSchedule,
    clientCallAttempt1: false,
    // HN-PSWOA-6253 | Added client id and client name in the payload to support BE requirements
    clientId: project?.clientId ?? null,
    clientName: project?.clientName ?? null,
    clientTextAttempt1: project?.clientTextAttempt1,
    clientEmailAttempt1: project?.clientEmailAttempt1,
    clientLvmAttempt1: project?.clientLvmAttempt1,
    response1: ContactAttemptOptions.find(c => c.value === project?.clientResponse1),
    response2: ContactAttemptOptions.find(c => c.value === project?.clientResponse2),
    clientCallAttempt2: false,
    clientTextAttempt2: project?.clientTextAttempt2,
    clientEmailAttempt2: project?.clientEmailAttempt2,
    clientLvmAttempt2: project?.clientLvmAttempt2,
    invoiceLink: project?.invoiceLink,

    invoiceAttachment: project.documents?.[0],
    finalSOWAmount: project?.finalSOWAmount,
    invoiceNumber: project?.invoiceNumber,
    paymentTerms: findOptionByValue(PAYMENT_TERMS_OPTIONS, project.paymentTerm),
    woaInvoiceDate: getLocalTimeZoneDate(project.woaInvoiceDate as string),
    woaExpectedPayDate: project.expectedPaymentDate as string,
    remainingPayment: remainingPayment < 0 ? 0 : remainingPayment,
    payment: '',
    lienRightFileDate: project.lienRightFileDate,
    lienRightExpireDate: project.lienRightExpireDate,
  }
}

const findOptionByValue = (options: SelectOption[], value: string | number | null): SelectOption | null =>
  options.find(option => option.value === value) || null

export const parseEditTicketFormValuesToPayload = async (formValues, projectData) => {
  const subWorkOrders = formValues.tradeAndDescription
    //?.filter(o => o?.isNew)
    ?.map(o => {
      return {
        tradeId: o.vendorTrade?.value,
        issueDescription: o.issueDescription,
        companyId: o.company?.value,
        workOrderId: o.id,
      }
    })

  const docs = [] as any
  const invoice = (await getFileContents(formValues?.invoiceAttachment, 42)) as DocumentType
  if (invoice?.fileObject) {
    docs.push(invoice)
  }

  const approvedOrSubmittedStatusMatches = [PROPERTY_STATUS.approved.value, PROPERTY_STATUS.submitted.value]
    .map(Number)
    .includes(projectData?.previousStatus)

  const revisionOrDeclinedStatusMatches = [PROPERTY_STATUS.revision.value, PROPERTY_STATUS.declined.value]
    .map(Number)
    .includes(projectData?.projectStatusId)

  const disputedStatusMatches = [PROPERTY_STATUS.disputed.value].map(Number).includes(projectData?.projectStatusId)

  //check to handle if project is in revision, declined or disputed & prevStatus value is Null
  // if this will true then we will send status SUBMITTED
  const existingRecordsCheck =
    (revisionOrDeclinedStatusMatches || disputedStatusMatches) && projectData?.previousStatus === null

  //Check if selected status/current status is equal to disputed and override status is equal to disputed
  // this check is to handle overrideProjectStatus and previousStatus
  const isDisputedSelected =
    Number(formValues.overrideProjectStatus?.value) === projectData?.projectStatusId ||
    formValues.overrideProjectStatus?.value === undefined

  //check to handle if prevstatus is approved/submitted OR projectStatus is disputed and user is again selecting disputed to concontrol override status history
  const wasApprovedOrSubmitted =
    approvedOrSubmittedStatusMatches || revisionOrDeclinedStatusMatches || isDisputedSelected

  return {
    forcedStatus: formValues?.forcedStatus,
    projectType: 105,
    projectStatus: formValues.status?.label,
    projectStatusId: formValues.status?.value,
    overrideProjectStatus: isDisputedSelected ? null : Number(formValues.overrideProjectStatus?.value),
    sowOriginalContractAmount: formValues.sowOriginalContractAmount,
    propertyId: Number(formValues?.propertyId),
    woNumber: formValues?.ticketId,
    //order intake
    id: formValues?.id,
    subWorkOrders: subWorkOrders,
    taskPriority: formValues.priority?.value,
    projectManagerId: formValues.maintenanceSpecialist?.value,
    projectManagerName: formValues.maintenanceSpecialist?.label,
    projectCoordinatorName: formValues.maintenanceCoordinator?.label,
    projectCordinatorId: formValues.maintenanceCoordinator?.value,
    defaultMaintenanceCordinatorId: formValues.defaultMaintenanceCoordinator?.value,
    clientNteAmount: formValues.clientNTE,
    clientId: formValues?.clientId ?? null,
    clientName: formValues?.clientName ?? null,
    clientNotes: formValues.clientNotes,
    clientSchedule: formValues.schedule,
    clientCallAttempt1: false,
    clientTextAttempt1: formValues.clientTextAttempt1,
    clientEmailAttempt1: formValues.clientEmailAttempt1,
    clientLvmAttempt1: formValues.clientLvmAttempt1,
    clientResponse1: formValues.response1?.value,
    clientResponse2: formValues.response2?.value,
    clientCallAttempt2: false,
    clientTextAttempt2: formValues.clientTextAttempt2,
    clientEmailAttempt2: formValues.clientEmailAttempt2,
    clientLvmAttempt2: formValues.clientLvmAttempt2,
    previousStatus: existingRecordsCheck
      ? Number(PROPERTY_STATUS.submitted.value)
      : wasApprovedOrSubmitted
      ? projectData?.previousStatus
      : projectData?.projectStatusId,
    isRevised: projectData?.isRevised,
    isDeclined: projectData?.isDeclined,

    invoiceLink: formValues?.invoiceLink,

    // Lien values
    lienRightFileDate: convertDateTimeToServerISO(formValues.lienRightFileDate),
    lienRightExpireDate: formValues.lienRightExpireDate,

    // Invoicing and payment payload
    documents: docs,

    sowNewAmount: formValues?.finalSOWAmount,
    invoiceNumber: formValues?.invoiceNumber,
    paymentTerm: formValues?.paymentTerms?.value || null,
    woaInvoiceDate: dateISOFormat(formValues?.woaInvoiceDate),
    expectedPaymentDate: dateISOFormat(formValues?.woaExpectedPayDate),
    remainingPayment: formValues?.remainingPayment,
    newPartialPayment: formValues?.payment,
    ...(formValues?.tradeAndDescription?.length &&
    formValues?.tradeAndDescription[0] &&
    formValues?.tradeAndDescription[0]?.id
      ? { statusByWorkOrder: formValues?.tradeAndDescription[0]?.id }
      : {}), // HN-PSWOA-5861 | BE was expecting this key-value pair to connect the ticket with trade, description & scheduled date data
  }
}

export const useTicketWorkOrders = ticketId => {
  const client = useClient()

  return useQuery<ProjectWorkOrderType[]>(
    ['GetProjectWorkOrders', ticketId],
    async () => {
      const response = await client(`project/${ticketId}/workorders`, {})

      return orderBy(
        response?.data,
        [
          item => {
            const createdDate = new Date(item.createdDate)
            return createdDate
          },
        ],
        ['asc'],
      )
    },
    {
      onSuccess: resp => {
        //console.log(resp)
      },
      enabled: !!ticketId,
    },
  )
}
